import { createContext, lazy, Suspense, useCallback, useContext, useState } from "react";
import { posthog, posthogLoaded } from "@/lib/posthog";

import { pageTypeFor } from "@/lib/analytics";
import { countAction } from "@/lib/audience";

const WaitlistDialog = lazy(() => import("@/components/WaitlistDialog"));

type WaitlistCtx = { openWaitlist: (source?: string, email?: string) => void };

const Ctx = createContext<WaitlistCtx>({ openWaitlist: () => {} });

export const useWaitlist = () => useContext(Ctx);

/** Provee un único diálogo de lista de espera accesible desde cualquier CTA
 *  vía useWaitlist().openWaitlist("origen", email?). El origen etiqueta el
 *  evento de PostHog y la columna `source` de signup_interest; `email` permite
 *  prerrellenarlo (p. ej. el que ya escribió en el gate de login). */
export function WaitlistProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<{ open: boolean; source?: string; email?: string }>({ open: false });

  const openWaitlist = useCallback((source?: string, email?: string) => {
    if (posthogLoaded()) {
      posthog().capture("waitlist_cta_click", {
        source: source ?? "web",
        path: window.location.pathname,
        ...pageTypeFor(window.location.pathname),
      });
    }
    countAction("waitlist_cta");
    setState({ open: true, source, email });
  }, []);

  return (
    <Ctx.Provider value={{ openWaitlist }}>
      {children}
      {state.open && (
        <Suspense fallback={null}>
          <WaitlistDialog
            open={state.open}
            source={state.source}
            initialEmail={state.email}
            onOpenChange={(o) => setState((s) => ({ ...s, open: o }))}
          />
        </Suspense>
      )}
    </Ctx.Provider>
  );
}
